import { createCollection } from '#pplx/frontend/apps/ask/queries/mutations/collectionMutations.js';
import { TEMP_COLLECTION_UUID } from '#pplx/frontend/apps/ask/queries/query-keys/collectionConstants.js';
import { getUserCollectionsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/collectionKeys.js';
import { getAllSpacesQueryKey, getListAtMentionsSpacesQueryKey, getSpacesLandingQueryKey, getSpacesLandingV2QueryKey, getUserPinnedSpacesQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/spacesKeys.js';
import { getSidebarV2InitialQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { WRITABLE_SPACES_QUERY_KEY } from '#pplx/frontend/apps/ask/queries/spaces-queries/constants.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
const CREATE_COLLECTION_MUTATION_KEY = ['createCollection'];
function getCreateCollectionMutationKey(reason) {
    return [...CREATE_COLLECTION_MUTATION_KEY, reason];
}
export function useIsCreateCollectionPending(reason) {
    return (useIsMutating({
        exact: true,
        mutationKey: getCreateCollectionMutationKey(reason),
    }) > 0);
}
// Guards require optimism to be explicitly disabled: a stale result skips the refresh that
// removes temporary rows. mutateAsync still resolves stale results; callers must guard their
// own side effects after awaiting it.
export const useCreateCollection = (options) => {
    const { optimisticallyUpdateUserCollections = true, reason } = options;
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: getCreateCollectionMutationKey(reason),
        mutationFn: async ({ title, description, emoji, appearance, instructions, accessLevel, template_id, projectBrainAutoUpdateEnabled, projectCode, callback, creationContext, isResultCurrent, }) => {
            const collection = await createCollection({
                params: {
                    title,
                    description,
                    emoji,
                    appearance,
                    instructions,
                    accessLevel,
                    template_id,
                    projectBrainAutoUpdateEnabled,
                    projectCode,
                    creationContext,
                },
                reason,
            });
            if (isResultCurrent?.() !== false)
                callback?.(collection);
            return { collection };
        },
        onMutate: ({ callback: _callback, creationContext: _creationContext, isResultCurrent: _isResultCurrent, template_id: _template_id, projectBrainAutoUpdateEnabled, projectCode: _projectCode, ...form }) => {
            if (!optimisticallyUpdateUserCollections)
                return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY_MIGRATION: Please fix in passing
            queryClient.setQueryData(getUserCollectionsQueryKey(), (oldData) => {
                oldData = oldData || { pages: [], pageParams: [] };
                const oldPages = oldData.pages[0] || [];
                const newCollWithTmpId = {
                    ...(oldPages.length > 0 ? oldPages[0] : {}),
                    ...form,
                    ...(projectBrainAutoUpdateEnabled !== undefined
                        ? { project_brain_auto_update_enabled: projectBrainAutoUpdateEnabled }
                        : {}),
                    uuid: TEMP_COLLECTION_UUID,
                    thread_count: 0,
                };
                return {
                    ...oldData,
                    pages: [[newCollWithTmpId, ...oldPages]],
                };
            });
        },
        onSuccess: async (_data, { isResultCurrent }) => {
            if (isResultCurrent?.() === false)
                return;
            queryClient.refetchQueries({ queryKey: getUserCollectionsQueryKey() });
            queryClient.invalidateQueries({
                queryKey: getListAtMentionsSpacesQueryKey(),
            });
            queryClient.invalidateQueries({ queryKey: getUserPinnedSpacesQueryKey() });
            queryClient.invalidateQueries({ queryKey: getSpacesLandingQueryKey() });
            queryClient.invalidateQueries({ queryKey: getSpacesLandingV2QueryKey() });
            queryClient.invalidateQueries({ queryKey: getSidebarV2InitialQueryKey() });
            queryClient.invalidateQueries({ queryKey: getAllSpacesQueryKey() });
            queryClient.invalidateQueries({
                queryKey: QueryClientHelper.makeQueryKey(WRITABLE_SPACES_QUERY_KEY),
            });
        },
    });
};
//# sourceMappingURL=useCreateCollection.js.map